import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const emptyItem = () => ({ description:'', qty:'', unit:'', unit_price:'', total:'' })

export default function Procurement() {
  const { user, profile, isAdmin, isAdminOrManager, canEditData } = useAuth()
  const [tab, setTab] = useState('list')
  const [orders, setOrders] = useState([])
  const [approvedReqs, setApprovedReqs] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [showForm, setShowForm] = useState(false)

  const [form, setForm] = useState({
    po_number:'', order_date:ugandaDate(),
    needed_by:'', supplier:'', supplier_id:'',
    requisition_id:'', reason:'',
    urgency:'Normal — standard lead time',
    payment_terms_days: 30,
    items:[emptyItem()],
  })

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    const [ordRes, reqRes, supRes] = await Promise.all([
      supabase.from('purchase_orders')
        .select('*, profiles!ordered_by(full_name), approver:profiles!approved_by(full_name), requisitions(req_number)')
        .order('created_at',{ascending:false}).limit(50),
      supabase.from('requisitions').select('id,req_number,items,urgency').eq('status','Approved'),
      supabase.from('suppliers').select('id,name,payment_terms_days').eq('is_active',true).order('name'),
    ])
    setOrders(ordRes.data||[])
    setApprovedReqs(reqRes.data||[])
    setSuppliers(supRes.data||[])
    setLoading(false)
  }

  const setItem = (i, field, val) => {
    const items = [...form.items]
    items[i] = { ...items[i], [field]:val }
    if (field==='qty'||field==='unit_price') {
      const qty = parseFloat(field==='qty'?val:items[i].qty)||0
      const price = parseFloat(field==='unit_price'?val:items[i].unit_price)||0
      items[i].total = (qty*price).toFixed(0)
    }
    setForm(f=>({...f,items}))
  }

  const handleReqSelect = (reqId) => {
    const req = approvedReqs.find(r=>r.id===reqId)
    if (!req) { setForm(f=>({...f,requisition_id:''})); return }
    // Pre-fill items from requisition
    const items = (req.items||[]).map(i=>({
      description: i.item_name||i.description||'',
      qty: i.qty_requested||i.qty||'',
      unit: i.unit||'',
      unit_price:'', total:''
    }))
    setForm(f=>({...f, requisition_id:reqId, urgency:req.urgency||f.urgency, items:items.length?items:[emptyItem()]}))
  }

  const handleSupplierSelect = (supId) => {
    const sup = suppliers.find(s=>s.id===supId)
    setForm(f=>({...f, supplier_id:supId, supplier:sup?.name||'', payment_terms_days:sup?.payment_terms_days||30}))
  }

  const total = form.items.reduce((s,r)=>s+(parseFloat(r.total)||0),0)

  const nextPONum = () => {
    const nums = orders.map(o=>parseInt(o.po_number?.replace(/\D/g,''))).filter(Boolean)
    const next = nums.length?Math.max(...nums)+1:1
    return `PO-${new Date().getFullYear()}-${String(next).padStart(3,'0')}`
  }

  const handleSave = async () => {
    if (!form.supplier) { setMsg({type:'error',text:'Supplier is required.'}); return }
    if (!form.requisition_id) { setMsg({type:'error',text:'A Purchase Order must be linked to an approved Requisition. Please approve a requisition first.'}); return }
    setSaving(true); setMsg(null)
    const po_number = form.po_number||nextPONum()
    const { error } = await supabase.from('purchase_orders').insert({
      ordered_by: user.id,
      po_number,
      supplier: form.supplier,
      supplier_id: form.supplier_id||null,
      order_date: form.order_date,
      needed_by: form.needed_by||null,
      items: form.items.filter(i=>i.description),
      total_value: total,
      reason: form.reason,
      urgency: form.urgency,
      requisition_id: form.requisition_id,
      payment_terms_days: form.payment_terms_days,
      is_approved: false,
      finance_approved: false,
    })
    if (error) { setMsg({type:'error',text:error.message}); setSaving(false); return }
    // Mark requisition as converted
    await supabase.from('requisitions').update({status:'Converted to PO'}).eq('id',form.requisition_id)
    setMsg({type:'success',text:`Purchase Order ${po_number} created. Finance approval required before payment.`})
    setSaving(false); setShowForm(false); loadData()
    setTimeout(()=>setMsg(null),5000)
  }

  const handleApprove = async (id) => {
    await supabase.from('purchase_orders').update({is_approved:true,approved_by:user.id,approved_at:new Date().toISOString()}).eq('id',id)
    loadData()
  }

  const handleFinanceApprove = async (id) => {
    await supabase.from('purchase_orders').update({finance_approved:true,finance_approved_by:user.id,finance_approved_at:new Date().toISOString()}).eq('id',id)
    loadData()
  }

  const handlePrint = (po) => {
    const w = window.open('','_blank')
    w.document.write(`<html><head><title>Purchase Order ${po.po_number}</title>
    <style>
      body{font-family:Arial,sans-serif;padding:32px;color:#1C3520;}
      h1{font-size:1.4rem;margin-bottom:4px;}
      .sub{color:#666;font-size:0.85rem;margin-bottom:24px;}
      table{width:100%;border-collapse:collapse;margin-bottom:20px;}
      th{background:#1C3520;color:white;padding:8px 12px;text-align:left;font-size:0.82rem;}
      td{padding:8px 12px;border-bottom:1px solid #ddd;font-size:0.85rem;}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px;}
      .label{font-size:0.72rem;text-transform:uppercase;letter-spacing:0.1em;color:#666;margin-bottom:3px;}
      .val{font-weight:600;}
      .total{text-align:right;font-size:1.1rem;font-weight:700;margin-top:8px;}
      @media print{body{padding:16px;}}
    </style></head><body>
    <h1>📦 PURCHASE ORDER — ${po.po_number}</h1>
    <div class="sub">Kobflo Greentek Ltd · app.kobfloagri.com</div>
    <div class="grid">
      <div><div class="label">Date</div><div class="val">${new Date(po.order_date).toLocaleDateString('en-UG')}</div></div>
      <div><div class="label">Needed By</div><div class="val">${po.needed_by?new Date(po.needed_by).toLocaleDateString('en-UG'):'—'}</div></div>
      <div><div class="label">Supplier</div><div class="val">${po.supplier}</div></div>
      <div><div class="label">Payment Terms</div><div class="val">${po.payment_terms_days||30} days</div></div>
      <div><div class="label">Ordered By</div><div class="val">${po.profiles?.full_name||'—'}</div></div>
      <div><div class="label">Requisition Ref</div><div class="val">${po.requisitions?.req_number||'—'}</div></div>
    </div>
    <table>
      <thead><tr><th>Description</th><th>Qty</th><th>Unit</th><th>Unit Price (UGX)</th><th>Total (UGX)</th></tr></thead>
      <tbody>
        ${(po.items||[]).map(i=>`<tr><td>${i.description}</td><td>${i.qty}</td><td>${i.unit||'—'}</td><td>${parseFloat(i.unit_price||0).toLocaleString()}</td><td>${parseFloat(i.total||0).toLocaleString()}</td></tr>`).join('')}
      </tbody>
    </table>
    <div class="total">TOTAL VALUE: UGX ${(po.total_value||0).toLocaleString()}</div>
    <div style="margin-top:32px;display:grid;grid-template-columns:1fr 1fr;gap:32px;">
      <div><div class="label">Authorised By</div><div style="border-bottom:2px solid #ccc;margin-top:40px;"></div><div style="font-size:0.75rem;color:#888;margin-top:4px;">Name: ________________ &nbsp; Date: ________</div></div>
      <div><div class="label">Supplier Acknowledgement</div><div style="border-bottom:2px solid #ccc;margin-top:40px;"></div><div style="font-size:0.75rem;color:#888;margin-top:4px;">Name: ________________ &nbsp; Date: ________</div></div>
    </div>
    </body></html>`)
    w.document.close(); w.print()
  }

  return (
    <AppLayout title="Purchase Orders">
      <div className="section-header">
        <div className="section-icon">📦</div>
        <div className="section-info">
          <h2>Purchase Orders</h2>
          <p>POs must be linked to an approved Requisition. Finance approval required before payment.</p>
        </div>
        {canEditData && (
          <button className="btn btn-primary" style={{marginLeft:'auto'}} onClick={()=>setShowForm(true)}>
            + New Purchase Order
          </button>
        )}
      </div>

      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      <div className="tabs">
        <button className={`tab ${tab==='list'?'active':''}`} onClick={()=>setTab('list')}>📋 All POs</button>
        <button className={`tab ${tab==='suppliers'?'active':''}`} onClick={()=>setTab('suppliers')}>🏢 Suppliers</button>
      </div>

      {/* New PO Form */}
      {showForm && (
        <div className="card" style={{marginBottom:16,border:'2px solid var(--amber)'}}>
          <div className="card-title">New Purchase Order</div>
          <div className="card-subtitle">Must be linked to an approved Requisition. Items pre-fill from the requisition.</div>

          {approvedReqs.length===0 && (
            <div className="alert alert-red" style={{marginBottom:14}}>
              ⚠️ No approved Requisitions found. Please submit and approve a Requisition before creating a PO.
            </div>
          )}

          <div className="grid-2" style={{marginBottom:14}}>
            <div className="form-group">
              <label className="form-label">Linked Requisition *</label>
              <select className="form-select" value={form.requisition_id} onChange={e=>handleReqSelect(e.target.value)}>
                <option value="">— Select approved requisition —</option>
                {approvedReqs.map(r=><option key={r.id} value={r.id}>{r.req_number} ({r.items?.length||0} items)</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Supplier *</label>
              <select className="form-select" value={form.supplier_id} onChange={e=>handleSupplierSelect(e.target.value)}>
                <option value="">— Select supplier —</option>
                {suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {!form.supplier_id && (
                <input className="form-input" style={{marginTop:6}} placeholder="Or type supplier name" value={form.supplier} onChange={e=>setForm(f=>({...f,supplier:e.target.value}))} />
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Order Date</label>
              <input className="form-input" type="date" value={form.order_date} onChange={e=>setForm(f=>({...f,order_date:e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Needed By</label>
              <input className="form-input" type="date" value={form.needed_by} onChange={e=>setForm(f=>({...f,needed_by:e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Payment Terms (days)</label>
              <input className="form-input" type="number" value={form.payment_terms_days} onChange={e=>setForm(f=>({...f,payment_terms_days:parseInt(e.target.value)||30}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Urgency</label>
              <select className="form-select" value={form.urgency} onChange={e=>setForm(f=>({...f,urgency:e.target.value}))}>
                <option>Normal — standard lead time</option>
                <option>Urgent — needed within 48 hours</option>
                <option>Critical — needed immediately</option>
              </select>
            </div>
          </div>

          <div style={{fontWeight:700,marginBottom:10}}>Items (pre-filled from Requisition — add prices)</div>
          <div className="table-wrap" style={{marginBottom:8}}>
            <table>
              <thead><tr><th>Description</th><th>Qty</th><th>Unit</th><th>Unit Price (UGX)</th><th>Total (UGX)</th><th style={{width:32}}></th></tr></thead>
              <tbody>
                {form.items.map((item,i)=>(
                  <tr key={i}>
                    <td><input className="form-input" value={item.description} onChange={e=>setItem(i,'description',e.target.value)} /></td>
                    <td><input className="form-input" style={{width:70}} type="number" value={item.qty} onChange={e=>setItem(i,'qty',e.target.value)} /></td>
                    <td><input className="form-input" style={{width:70}} value={item.unit} onChange={e=>setItem(i,'unit',e.target.value)} /></td>
                    <td><input className="form-input" style={{width:110}} type="number" value={item.unit_price} onChange={e=>setItem(i,'unit_price',e.target.value)} /></td>
                    <td><input className="form-input" style={{width:110,fontWeight:700,background:'#f8f8f0'}} value={item.total?parseInt(item.total).toLocaleString():''} readOnly /></td>
                    <td>{form.items.length>1&&<button className="btn-del-row" onClick={()=>setForm(f=>({...f,items:f.items.filter((_,j)=>j!==i)}))}>✕</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn btn-secondary btn-sm" style={{marginBottom:14}} onClick={()=>setForm(f=>({...f,items:[...f.items,emptyItem()]}))}>+ Add Item</button>

          <div style={{background:'var(--amber-pale)',borderRadius:8,padding:12,marginBottom:14,textAlign:'right'}}>
            <span style={{fontFamily:"'DM Mono',monospace",fontSize:'0.75rem',color:'var(--text-muted)'}}>TOTAL VALUE: </span>
            <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:'1.4rem',fontWeight:700,color:'var(--green-deep)'}}>UGX {total.toLocaleString()}</span>
          </div>

          <div style={{display:'flex',gap:10}}>
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>{saving?<><span className="spinner"/>Saving...</>:'💾 Save Purchase Order'}</button>
            <button className="btn btn-ghost" onClick={()=>setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {tab==='list' && (
        <div className="card">
          {loading?<div style={{textAlign:'center',padding:32}}><span className="spinner dark"/></div>
          :orders.length===0?<div className="empty-state"><div className="empty-icon">📦</div><h3>No purchase orders yet</h3></div>
          :(
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>PO #</th><th>Date</th><th>Supplier</th><th>Req #</th><th>Items</th><th>Total (UGX)</th>
                    <th>Mgmt</th><th>Finance</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(po=>(
                    <tr key={po.id}>
                      <td style={{fontFamily:"'DM Mono',monospace",fontWeight:600}}>{po.po_number}</td>
                      <td style={{fontSize:'0.78rem'}}>{new Date(po.order_date).toLocaleDateString('en-UG')}</td>
                      <td>{po.supplier}</td>
                      <td style={{fontSize:'0.75rem',fontFamily:"'DM Mono',monospace",color:'var(--text-muted)'}}>{po.requisitions?.req_number||'—'}</td>
                      <td style={{fontSize:'0.78rem'}}>{po.items?.length||0} items</td>
                      <td style={{fontWeight:600}}>{(po.total_value||0).toLocaleString()}</td>
                      <td>
                        {po.is_approved
                          ? <span className="badge badge-green">✓ Approved</span>
                          : isAdminOrManager
                            ? <button className="btn btn-secondary btn-sm" style={{color:'var(--green-mid)'}} onClick={()=>handleApprove(po.id)}>✓ Approve</button>
                            : <span className="badge badge-amber">Pending</span>
                        }
                      </td>
                      <td>
                        {po.finance_approved
                          ? <span className="badge badge-green">✓ Finance</span>
                          : isAdmin
                            ? <button className="btn btn-secondary btn-sm" style={{color:'var(--blue)'}} onClick={()=>handleFinanceApprove(po.id)}>✓ Finance</button>
                            : <span className="badge badge-amber">Pending</span>
                        }
                      </td>
                      <td>
                        <button className="btn btn-secondary btn-sm" onClick={()=>handlePrint(po)}>🖨️ Print</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab==='suppliers' && <SuppliersTab suppliers={suppliers} orders={orders} user={user} isAdmin={isAdmin} onRefresh={loadData} />}
    </AppLayout>
  )
}

function SuppliersTab({ suppliers, orders, user, isAdmin, onRefresh }) {
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ name:'', contact_person:'', phone:'', email:'', address:'', payment_terms_days:30, notes:'' })
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  const handleSave = async () => {
    if (!form.name) { setMsg({type:'error',text:'Supplier name is required.'}); return }
    setSaving(true)
    const { error } = await supabase.from('suppliers').insert({ ...form, payment_terms_days:parseInt(form.payment_terms_days)||30 })
    if (error) { setMsg({type:'error',text:error.message}); setSaving(false); return }
    setMsg({type:'success',text:'Supplier added.'}); setSaving(false); setShowForm(false)
    onRefresh(); setTimeout(()=>setMsg(null),3000)
  }

  // Calculate outstanding per supplier from approved POs
  const supplierOutstanding = {}
  orders.forEach(po => {
    if (po.supplier && po.is_approved) {
      supplierOutstanding[po.supplier] = (supplierOutstanding[po.supplier]||0) + (po.total_value||0)
    }
  })

  return (
    <div>
      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}
      {isAdmin && (
        <div style={{marginBottom:12}}>
          <button className="btn btn-primary" onClick={()=>setShowForm(s=>!s)}>+ Add Supplier</button>
        </div>
      )}
      {showForm && (
        <div className="card" style={{marginBottom:16,border:'1px solid var(--amber)'}}>
          <div className="card-title">New Supplier</div>
          <div className="grid-2" style={{marginBottom:12}}>
            <div className="form-group"><label className="form-label">Supplier Name *</label><input className="form-input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Contact Person</label><input className="form-input" value={form.contact_person} onChange={e=>setForm(f=>({...f,contact_person:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Payment Terms (days)</label><input className="form-input" type="number" value={form.payment_terms_days} onChange={e=>setForm(f=>({...f,payment_terms_days:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Address</label><input className="form-input" value={form.address} onChange={e=>setForm(f=>({...f,address:e.target.value}))} /></div>
          </div>
          <div style={{display:'flex',gap:10}}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Saving...':'Save Supplier'}</button>
            <button className="btn btn-ghost" onClick={()=>setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-title">Supplier Accounts — Outstanding Balances</div>
        <div className="card-subtitle">Total outstanding across all approved POs per supplier.</div>
        {suppliers.length===0
          ? <div className="empty-state"><div className="empty-icon">🏢</div><h3>No suppliers yet</h3></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Supplier</th><th>Contact</th><th>Phone</th><th>Terms</th><th>Outstanding POs (UGX)</th></tr></thead>
                <tbody>
                  {suppliers.map(s=>(
                    <tr key={s.id}>
                      <td style={{fontWeight:600}}>{s.name}</td>
                      <td style={{fontSize:'0.8rem'}}>{s.contact_person||'—'}</td>
                      <td style={{fontSize:'0.8rem',fontFamily:"'DM Mono',monospace"}}>{s.phone||'—'}</td>
                      <td style={{fontSize:'0.78rem'}}>{s.payment_terms_days} days</td>
                      <td style={{fontWeight:700,color:(supplierOutstanding[s.name]||0)>0?'var(--red)':'var(--green-mid)'}}>{(supplierOutstanding[s.name]||0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>
    </div>
  )
}
